/**
 * Логика редактора тарифной лестницы. Вынесена из компонентов, чтобы её можно
 * было проверить тестами: цена — не то место, где ошибку заметят сразу.
 */

export const MILE_TYPES = [
  { value: "FIRST", label: "Первая миля" },
  { value: "MIDDLE", label: "Магистраль" },
  { value: "LAST", label: "Последняя миля" },
  { value: "INTRACITY", label: "Внутригород" },
];

export const mileLabel = (value) =>
  MILE_TYPES.find((m) => m.value === value)?.label ?? value ?? "—";

/** Строка редактора: числа держим строками, чтобы не терять ввод на полпути. */
export const toEditorRows = (points) =>
  (points ?? []).map((point, index) => ({
    key: `${index}-${point.weight}`,
    weight: asText(point.weight),
    price: asText(point.price),
    priceBreak: asText(point.priceBreak),
    weightBreak: asText(point.weightBreak),
  }));

export const emptyRow = () => ({
  key: `new-${Math.random().toString(36).slice(2)}`,
  weight: "",
  price: "",
  priceBreak: "0",
  weightBreak: "0",
});

/**
 * Строки редактора в точки для отправки. Пустые строки целиком отбрасываются:
 * оператор мог добавить строку и передумать.
 */
export const toLadderPoints = (rows) =>
  (rows ?? [])
    .filter((row) => !isBlankRow(row))
    .map((row) => ({
      weight: toNumber(row.weight),
      price: toNumber(row.price),
      priceBreak: toNumber(row.priceBreak),
      weightBreak: toNumber(row.weightBreak),
    }));

const isBlankRow = (row) =>
  [row.weight, row.price].every((value) => String(value ?? "").trim() === "");

/**
 * Проверки, которые видно до отправки. Сервер проверяет то же самое и остаётся
 * последним словом — здесь это лишь чтобы не гонять заведомо плохое.
 */
export const validateRows = (rows) => {
  const points = toLadderPoints(rows);
  const problems = [];

  if (points.length === 0) {
    problems.push("Лестница пуста: добавьте хотя бы одну точку.");
    return problems;
  }

  points.forEach((point, index) => {
    const at = `строка ${index + 1}`;
    if (!Number.isFinite(point.weight) || point.weight <= 0) {
      problems.push(`${at}: вес должен быть больше нуля`);
    }
    if (!Number.isFinite(point.price) || point.price < 0) {
      problems.push(`${at}: цена не может быть отрицательной`);
    }
    if (point.priceBreak < 0 || point.weightBreak < 0) {
      problems.push(`${at}: надбавка и шаг не могут быть отрицательными`);
    }
  });

  const weights = points.map((p) => p.weight);
  const duplicates = weights.filter((w, i) => weights.indexOf(w) !== i);
  [...new Set(duplicates)].forEach((weight) =>
    problems.push(`вес ${weight} кг указан дважды — точка лестницы должна быть одна`)
  );

  return problems;
};

/**
 * Сводка ответа сервера. Немонотонная цена и расхождение лестницы разведены
 * намеренно: первое означает поломку расчёта, второе — противоречие в самом
 * тарифе перевозчика, и адресаты у них разные.
 */
export const buildLadderSummary = (response) => ({
  saved: Boolean(response?.saved),
  pointsBefore: response?.pointsBefore ?? 0,
  pointsAfter: response?.pointsAfter ?? 0,
  rejected: response?.rejected ?? [],
  monotonicityViolations: response?.probe?.monotonicityViolations ?? 0,
  ladderInconsistencies: response?.probe?.ladderInconsistencies ?? 0,
  probe: response?.probe ?? null,
});

const asText = (value) =>
  value === null || value === undefined ? "" : String(value);

const toNumber = (value) => {
  const normalized = String(value ?? "").trim().replace(",", ".");
  if (normalized === "") return 0;
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? NaN : parsed;
};
